/**
 * Centralized page metadata factory.
 *
 * Mirrors the reference repos' lib/seo.ts `pageMetadata()`: pages describe their
 * SEO declaratively (title, description, image, breadcrumbs, extra schema) and this
 * returns a normalized props object spread into <SEO {...} />. The <SEO> component
 * is the thin runtime renderer that injects tags into <head> (captured by the SSG
 * prerender) — a Vite SPA can't use Next's server generateMetadata, so this is the
 * faithful adaptation of the same pattern.
 */

import {
  organizationSchema,
  webSiteSchema,
  breadcrumbSchema,
  type BreadcrumbItem,
} from './structured-data';
import { SITE_CONFIG } from '@/config/site';

export const SITE = {
  url: SITE_CONFIG.url,
  name: SITE_CONFIG.name,
  defaultImage: SITE_CONFIG.ogImage,
  twitter: SITE_CONFIG.twitterHandle,
} as const;

export interface PageMetaInput {
  /** Page title. Translations already carry the brand, so it's passed through as-is. */
  title: string;
  description: string;
  /** Absolute or site-relative OG/Twitter image. Defaults to the site OG image. */
  image?: string;
  /** OG type (default "website"; use "article" on article pages). */
  type?: string;
  noindex?: boolean;
  /** Breadcrumb trail — emitted as BreadcrumbList JSON-LD. */
  breadcrumbs?: BreadcrumbItem[];
  /** Extra schema object(s) (Service, Article, FAQPage, …) from structured-data.ts. */
  jsonLd?: object | object[];
  /** Include Organization + WebSite schema (use on the homepage). */
  includeOrganization?: boolean;
}

export interface PageMeta {
  title: string;
  description: string;
  image?: string;
  type?: string;
  noindex?: boolean;
  jsonLd: object[];
}

/** Compose a page's metadata + JSON-LD graph. */
export function pageMetadata(input: PageMetaInput): PageMeta {
  const schemas: object[] = [];

  if (input.includeOrganization) {
    schemas.push(organizationSchema(), webSiteSchema());
  }
  if (input.breadcrumbs?.length) {
    schemas.push(breadcrumbSchema(input.breadcrumbs));
  }
  if (input.jsonLd) {
    if (Array.isArray(input.jsonLd)) schemas.push(...input.jsonLd);
    else schemas.push(input.jsonLd);
  }

  return {
    title: input.title,
    description: input.description,
    image: input.image ?? SITE.defaultImage,
    type: input.type ?? 'website',
    noindex: input.noindex ?? false,
    jsonLd: schemas,
  };
}
